import React, { useEffect } from 'react';
import '../../App.css';
import { LEGAL_META } from '../../core/legal-policies';
import { InlineIcon } from '../../components/Icon';

function DeleteAccountPage({ onBack }) {
  useEffect(() => {
    document.title = `Delete account — ${LEGAL_META.brand}`;
    return () => { document.title = 'Founders Bridge — Legal & Compliance Portal'; };
  }, []);

  return (
    <div className="policy-page">
      <header className="policy-page-head">
        {onBack && (
          <button type="button" className="policy-page-back" onClick={onBack}>
            <InlineIcon name="back" size={13} gap={5}>Back</InlineIcon>
          </button>
        )}
        <div className="policy-page-title">{LEGAL_META.brand} — Delete account</div>
      </header>

      <div className="policy-page-body">
        <div className="card">
          <div className="card-head">
            <div className="card-title">Request deletion of your Founders Bridge account</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Founders Bridge mobile app &amp; client portal</div>
          </div>
          <div className="card-body" style={{ lineHeight: 1.8, fontSize: 14, color: 'var(--ink2)' }}>
            <p style={{ marginBottom: 20 }}>
              This page explains how users of the <strong>Founders Bridge</strong> app (published by <strong>{LEGAL_META.entity}</strong>)
              can request deletion of their account and associated personal data.
            </p>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              Option 1 — Delete in the app (client accounts)
            </h3>
            <ol style={{ margin: '0 0 20px', paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>Open the <strong>Founders Bridge</strong> app on your phone or tablet.</li>
              <li style={{ marginBottom: 8 }}>Sign in with your client account.</li>
              <li style={{ marginBottom: 8 }}>Open the <strong>sidebar menu</strong> (☰ on mobile).</li>
              <li style={{ marginBottom: 8 }}>Tap <strong>Delete account…</strong> at the bottom of the sidebar.</li>
              <li style={{ marginBottom: 8 }}>Type your registered email address to confirm.</li>
              <li style={{ marginBottom: 8 }}>Check the box confirming you understand the 30-day account freeze.</li>
              <li style={{ marginBottom: 8 }}>Tap <strong>Delete my account</strong>.</li>
            </ol>
            <p style={{ marginBottom: 20, fontSize: 13, color: 'var(--muted)' }}>
              In-app deletion is available for <strong>client</strong> accounts. Staff and administrator accounts must use Option 2 below.
            </p>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              Option 2 — Request by email
            </h3>
            <p style={{ marginBottom: 12 }}>
              Email us from the address registered on your account:
            </p>
            <p style={{ marginBottom: 20 }}>
              <strong>{LEGAL_META.email}</strong>
              <br />
              Subject: <strong>Account deletion request — Founders Bridge</strong>
              <br />
              Include: your full name, registered email, and phone number (if on file).
            </p>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              What happens after you request deletion
            </h3>
            <ul style={{ margin: '0 0 20px', paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>Your login is disabled immediately.</li>
              <li style={{ marginBottom: 8 }}>Your account is marked deleted and <strong>frozen for 30 days</strong>. You cannot sign in during this period.</li>
              <li style={{ marginBottom: 8 }}>After 30 days, the account remains deleted. Contact support if you need help restoring access.</li>
            </ul>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              Data deleted or deactivated
            </h3>
            <ul style={{ margin: '0 0 20px', paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>Portal login access and authentication (including Google/Apple sign-in for that account).</li>
              <li style={{ marginBottom: 8 }}>Your user profile is marked deleted and removed from active client portal use.</li>
              <li style={{ marginBottom: 8 }}>Active notifications and portal access tied to your user account.</li>
            </ul>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              Data we may retain (and why)
            </h3>
            <ul style={{ margin: '0 0 20px', paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Invoices, payment records, and tax/compliance records</strong> — retained as required for accounting, audit, and legal obligations.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Documents and task submissions</strong> already provided for an engagement — may be retained where needed for statutory compliance and service delivery records.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Support tickets and correspondence</strong> — may be retained for dispute resolution and quality records.
              </li>
              <li style={{ marginBottom: 8 }}>
                Retention follows applicable Indian law and our{' '}
                <a href="/policies/privacy" style={{ color: 'var(--blue)' }}>Privacy Policy</a>.
              </li>
            </ul>

            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>
              Contact
            </h3>
            <p style={{ marginBottom: 0 }}>
              {LEGAL_META.entity}<br />
              {LEGAL_META.email} · {LEGAL_META.phone}<br />
              {LEGAL_META.address}<br />
              Privacy policy: <a href="/policies" style={{ color: 'var(--blue)' }}>foundersbridge.app/policies</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export { DeleteAccountPage };
